import React from 'react'
import Tooltip from '../ui/Tooltip'
import { PartnerMetrics } from '../../types/partner'
import { formatCurrency } from '../../utils/formatters'

// The first two categorical slots, in the same order the broadcast charts use.
export const SOCIAL_COLOR = '#2a78d6'
export const BROADCAST_COLOR = '#eb6834'

/** Social EMV plus priced broadcast value. Unpriced broadcast adds nothing. */
export const valueDelivered = (m: PartnerMetrics | undefined): number =>
  Number(m?.total_emv ?? 0) + Number(m?.broadcast_value ?? 0)

interface Props {
  metrics: PartnerMetrics
  /** The largest value on the page; this bar's length is its share of it. */
  max: number
  showBroadcast: boolean
}

/**
 * One sponsor's value delivered, as a bar whose LENGTH is the total (scaled to
 * the largest sponsor on the page) and whose COLOURS are the social/broadcast
 * mix. The dollar total sits beside it, so the bar is never the only place the
 * number lives.
 *
 * UNPRICED BROADCAST IS SAID. A sponsor with broadcast exposure but no rate
 * would otherwise draw an all-social bar and read as "no broadcast value",
 * which is a different claim from "not priced yet".
 */
const ValueDeliveredBar: React.FC<Props> = ({ metrics, max, showBroadcast }) => {
  const social = Number(metrics.total_emv ?? 0)
  const broadcast = Number(metrics.broadcast_value ?? 0)
  const total = social + broadcast
  const broadcasts = metrics.broadcasts ?? 0
  const unpriced = metrics.broadcasts_unpriced ?? 0
  const pct = (n: number) => total > 0 ? `${Math.round((n / total) * 100)}%` : '0%'

  const lines = [`Social EMV: ${formatCurrency(social)} (${pct(social)})`]
  if (showBroadcast) {
    lines.push(metrics.broadcast_value == null
      ? broadcasts > 0 ? 'Broadcast: not priced yet' : 'Broadcast: no exposure'
      : `Broadcast value: ${formatCurrency(broadcast)} (${pct(broadcast)})`)
    if (metrics.broadcast_value != null && unpriced > 0) {
      lines.push(`${unpriced} of ${broadcasts} broadcasts not priced yet, so not included`)
    }
  }

  const width = total > 0 ? Math.max((total / max) * 100, 2) : 0

  return (
    <Tooltip
      placement="top"
      maxWidth={260}
      wrapperClassName="block"
      content={<div className="space-y-0.5">{lines.map((l) => <div key={l}>{l}</div>)}</div>}
    >
      <div className="flex items-center gap-2 min-w-[180px] cursor-default">
        <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
          {width > 0 && (
            <div className="flex h-full gap-[2px]" style={{ width: `${width}%` }}>
              {social > 0 && (
                <span className="block h-full first:rounded-l-full last:rounded-r-full"
                      style={{ flexGrow: social, flexBasis: 0, backgroundColor: SOCIAL_COLOR }} />
              )}
              {showBroadcast && broadcast > 0 && (
                <span className="block h-full first:rounded-l-full last:rounded-r-full"
                      style={{ flexGrow: broadcast, flexBasis: 0, backgroundColor: BROADCAST_COLOR }} />
              )}
            </div>
          )}
        </div>
        <span className="w-20 text-right text-xs font-medium text-gray-900 tabular-nums">
          {formatCurrency(total)}
        </span>
      </div>
      {showBroadcast && unpriced > 0 && (
        <p className="text-[10px] text-amber-700 mt-0.5">
          {metrics.broadcast_value == null
            ? 'Broadcast not priced'
            : `${unpriced} of ${broadcasts} broadcasts not priced`}
        </p>
      )}
    </Tooltip>
  )
}

export default ValueDeliveredBar
